// The other mode's job (with its labels) is fetched ahead of the switch. Annotations are not:
// steps or another user may change them meanwhile, so they always come fresh with the switch.
export type JobKind = 'task' | 'reference';

// past this the job's fields (state, assignee, labels) are refetched rather than trusted
export const PREFETCH_TTL_MS = 5 * 60 * 1000;

interface Entry {
    at: number;
    job: Promise<any>;
}

const entries = new Map<string, Entry>();
const keyOf = (kind: JobKind, jobId: number): string => `${kind}:${jobId}`;

export function prefetchJob(
    kind: JobKind,
    jobId: number,
    load: () => Promise<any>,
    warm: (job: any) => Promise<void> = async () => {},
    now = Date.now(),
): void {
    const key = keyOf(kind, jobId);
    const existing = entries.get(key);
    if (existing && now - existing.at < PREFETCH_TTL_MS) return;
    const job = load();
    const entry = { at: now, job };
    entries.set(key, entry);
    job.catch(() => {
        if (entries.get(key) === entry) entries.delete(key);
    });
    // a frame that fails to warm only costs the switch its head start
    job.then((instance) => warm(instance)).catch(() => {});
}

// a prefetched job is used once; a failed or stale prefetch falls back to a normal load
export async function prefetchedOr<T>(
    kind: JobKind, jobId: number, load: () => Promise<T>, now = Date.now(),
): Promise<T> {
    const key = keyOf(kind, jobId);
    const entry = entries.get(key);
    entries.delete(key);
    if (entry && now - entry.at < PREFETCH_TTL_MS) {
        try {
            return await entry.job;
        } catch {
            // fall through to a normal load
        }
    }
    return load();
}

export function clearPrefetchedJobs(): void {
    entries.clear();
}
